// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useState, type ReactNode } from 'react';

import type { CaseStatus } from '../../sager/model';
import { NO_SURVEY_TEXT } from '../../sager/model';
import { ProgressRing } from '../bygning/ProgressRing';
import { Pill } from '../Pill';
import styles from './CaseCard.module.css';

export interface CaseCardProps {
  /**
   * The case's Overblik. A plain href, not a router link: entering a case is
   * a page load (a fresh client and events socket per case, `app/cases.ts`).
   */
  href: string;
  name: string;
  subline: string;
  /** Approved of all types (the top bar's ring); null when there is no survey yet. */
  progress: { approved: number; total: number } | null;
  /** "54 % bevaring/genbrug", beside the ring; null with `progress`. */
  reuseText: string | null;
  status: CaseStatus;
  date: string;
  /** Small print: the file and its size. */
  fileLine?: string;
  /** The case is archived. */
  archived?: boolean;
  /**
   * A server-rendered plan (`GET /renders?view=plan`). It answers 422 when the
   * project has no cloud to draw and 503 when the server has no renderer; on
   * either (or any other failure) the thumb shows the status instead (R2).
   */
  thumbUrl: string;
  /**
   * Controls laid over the thumbnail's top-right corner (Sager's "Slet").
   * They sit beside the card's link, never inside it, so a press on one
   * does not open the case.
   */
  actions?: ReactNode;
}

/** One case on Sager: a floating card in the Bygning look, linking to the case's Overblik. */
export function CaseCard({
  href,
  name,
  subline,
  progress,
  reuseText,
  status,
  date,
  fileLine,
  archived,
  thumbUrl,
  actions,
}: CaseCardProps) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const id = useId();
  // Named by the case alone; the subline, status and date describe it.
  return (
    <div className={styles.wrap}>
      <a href={href} className={styles.card} aria-label={name} aria-describedby={`${id}-sub ${id}-foot`}>
        <div className={styles.thumb} data-plain={thumbFailed || undefined}>
          {thumbFailed ? (
            <span className={styles.thumbLabel}>{status.label}</span>
          ) : (
            <img
              className={styles.thumbImg}
              src={thumbUrl}
              alt="Plan af punktskyen"
              loading="lazy"
              onError={() => setThumbFailed(true)}
            />
          )}
        </div>
        <div className={styles.body}>
          <h2 className={styles.name}>{name}</h2>
          <p id={`${id}-sub`} className={styles.addr}>
            {subline}
          </p>
          {/* The ring and its words, then the reuse share on a line of its own. */}
          <p className={styles.progress}>
            {progress ? (
              <>
                <ProgressRing approved={progress.approved} total={progress.total} keepText />
                {reuseText && <span className={styles.reuse}>{reuseText}</span>}
              </>
            ) : (
              NO_SURVEY_TEXT
            )}
          </p>
          {fileLine && <p className={`${styles.file} mono`}>{fileLine}</p>}
          <div id={`${id}-foot`} className={styles.foot}>
            <Pill tone={status.tone}>{status.label}</Pill>
            {archived && <Pill tone="wait">Arkiveret</Pill>}
            <span className={styles.date}>{date}</span>
          </div>
        </div>
      </a>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
